import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import UpdateIcon from '@material-ui/icons/Update';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  updateDialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      padding: theme.spacing(2),
    },
  },
  updateIcon: {
    fontSize: 60,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  content: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  updateButton: {
    background: 'linear-gradient(135deg, #1e3a5f 0%, #003b8c 100%)',
    color: 'white',
    fontWeight: 'bold',
    padding: '12px 32px',
    '&:hover': {
      background: 'linear-gradient(135deg, #0d8a4e 0%, #054066 100%)',
    },
  },
  laterButton: {
    color: theme.palette.text.secondary,
  },
}));

const UpdateNotification = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showUpdate, setShowUpdate] = useState(false);
  const [newVersion, setNewVersion] = useState(null);

  useEffect(() => {
    // On first load, stamp the current version silently (no modal).
    // Only show the modal when the version changes WHILE the user is active.
    let initialVersionStamped = false;

    const checkForUpdates = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), {
          cache: 'no-store',
        });
        const data = await response.json();
        if (!data.version) return;

        const currentVersion = localStorage.getItem('app_version');
        const dismissedVersion = localStorage.getItem('dismissed_version');

        if (!currentVersion || !initialVersionStamped) {
          // First load or first check: just save the version, don't show modal
          localStorage.setItem('app_version', data.version);
          initialVersionStamped = true;
          return;
        }

        // Only show if version actually changed and wasn't dismissed
        if (currentVersion !== data.version && dismissedVersion !== data.version) {
          setNewVersion(data.version);
          setShowUpdate(true);
        }
      } catch (error) {
        // Network error, ignore silently
      }
    };

    // First check after 10 seconds (gives time to stamp initial version)
    const initialTimeout = setTimeout(checkForUpdates, 10000);

    // Then every 5 minutes (300s) – no need to check more often
    const interval = setInterval(checkForUpdates, 300000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  const handleUpdate = () => {
    // Limpiar el dismissal
    localStorage.removeItem('dismissed_version');
    
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }

    // Clear localStorage version
    localStorage.removeItem('app_version');

    // Hard reload
    window.location.reload(true);
  };

  const handleLater = () => {
    // Marcar esta versión como dismissed
    if (newVersion) {
      localStorage.setItem('dismissed_version', newVersion);
    }
    setShowUpdate(false);
  };

  if (!showUpdate) return null;

  return (
    <Dialog
      open={showUpdate}
      onClose={handleLater}
      className={classes.updateDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent className={classes.content}>
        <UpdateIcon className={classes.updateIcon} />
        <DialogTitle className={classes.title}>
          {t("updateNotification.title")}
        </DialogTitle>
        <Typography variant="body1" color="textSecondary">
          {t("updateNotification.subtitle")}
        </Typography>
        {newVersion && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {t("updateNotification.versionLabel", { version: newVersion })}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          {t("updateNotification.instruction")}
        </Typography>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', padding: '16px 24px 24px' }}>
        <Button
          onClick={handleLater}
          className={classes.laterButton}
        >
          {t("updateNotification.buttons.later")}
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          className={classes.updateButton}
          startIcon={<RefreshIcon />}
        >
          {t("updateNotification.buttons.updateNow")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateNotification;
